import {
  BenchmarkIndexPME,
  BenchmarkLiquidityGraphParams,
  ChartType,
  CurrencyType,
  DateRange,
  DateRangeValue,
  DealBase,
  IPortfolioConstructionCorrelationItem,
  QuickViewQueries,
  TableData
} from "..";

export interface IReports {
  meta: ReportsMeta;
  expandedRows: Set<string>;
  queries: ReportsQueries;
  tableData: ReportTableData | null;
  secondaryTableData: ReportTableData | null;
  quick_view_meta: {
    characteristics: string[];
    measures: IQuickViewMeasures[];
    dealFilters: ReportFilters;
    calculatedFields: string[];
  };
  refetchReports: RefetchReport | null;
  ignoreDefaultView: boolean;
  quickViewQueries: QuickViewQueries;
  is_hedge_fund: boolean;
  correlationMatrixSettings: CorrelationMatrixSettings;
}

export type IrrGraph = {
  xAxis: string[];
  series: { name: string | string[]; data: number[] }[];
  title: string;
};

export interface ICubicData {
  cubic1: CubicData;
  cubic2: CubicData;
  cubic3: CubicData;
  cubic4: CubicData;
  cubic5: CubicData;
}

export interface ReportsMeta {
  assetClasses: AssetClass[];
  characteristics: string[];
  measures: string[];
  measureClasses: MeasureClass[];
  dealFilters: ReportFilters;
  comparisonColumns: string[];
  calculatedFields: string[];
  deals: DealBase[];
  KPIFields: string[];
  KPICharacteristics: string[];
}

export interface IQuickViewMeasures {
  id: string;
  name: string;
  types: Array<"sum" | "last">;
  is_currency: boolean;
}

export interface QuickViewMeta extends Pick<ReportsMeta, "calculatedFields" | "characteristics" | "dealFilters"> {
  measures: IQuickViewMeasures[];
}

export type ReportFilters = Record<string, (number | string | boolean)[]>;

export type ReportTableData = {
  rows: ClientDealTableRow[];
  columns: DealTableColumn[];
  count_rows?: number;
};

export type DealTableRow = {
  title: string;
  deal_id?: number;
  postfix?: string;
  children?: Array<DealTableRow>;
  // data?: Record<string, string | number>; // Deprecated: kept for backward compatibility, use valueData instead
  postfixes?: Record<string, string>;
  valueData: Record<string, number | null>;
  pme_warning?: string;
};

export type DealTableColumn = {
  key: string;
  title: string;
  dateRange?: DateRange;
  postfix?: string;
};

export interface ClientDealTableRow extends DealTableRow {
  id?: string;
  children?: Array<ClientDealTableRow>;
  placed_on_market?: boolean;
  secondary_pricing_title?: string;
}

export type CalculatedField =
  | "average(mr)"
  | "dpi"
  | "exposure"
  | "irr"
  | "moic"
  | "multiple"
  | "sd(mr)"
  | "unfundedCommitment"
  | "deals"
  | "adjNav";

export type GroupBy = string[][];

export type RowBy = "deal" | "investor" | "measure" | "year";
export type DealTableRowBy = "measure" | "investor";
export type DealTableGroupByFields = "measure" | "measureClassLevel1" | "date" | "investor";
export type DealTableGroupBy = DealTableGroupByFields[][];
export type ReportTableColumnBy = "measure" | "year" | "quarter" | "month" | "investor" | "timeline";
export type ReportTableColumn = {
  key?: string;
  aggregation: string;
  dateRange?: DateRangeValue;
  currency?: CurrencyType;
  hedging?: ExchangeMethod;
  abbreviated?: boolean;
  decimal?: number;
  use_accounting_date?: boolean;
};

export type GraphInterval = "month" | "quarter" | "year";

export type ICubic = Record<string, CubicPayload>;

export type ChartSettings = {
  measure: string;
  groupBy: string;
  chartType?: ChartType;
  xAxis?: { name: string; type: string };
  yAxis?: { name: string; type: string };
};

export interface ReportsQueries<DateType = string> {
  clientId: string;
  filters: ReportFilters;
  // deal: Nullable<DealBase>;
  groupBy: GroupBy;
  rowBy: RowBy;
  columnBy: ReportTableColumnBy;
  reportTableColumns: ReportTableColumn[];
  classicTableColumns: ReportTableColumn[];
  measures: string[];
  characteristics: string[];
  secondaryPricing: string[];
  dateRange: DateRangeValue<DateType>;
  secondaryDateRange: DateRangeValue<DateType>;
  calculatedFields: string[];
  indexName?: string;
  comparisonColumns: string[];
  currency: CurrencyType;
  use_accounting_date: boolean;
  exchangeMethod: ExchangeMethod;
  originalCurrency: boolean;
  graphDataFormation: Record<string, ChartSettings>;
  filteredPortfolio?: boolean;
  // Deal table queries
  dealTableRowBy: DealTableRowBy;
  dealTableColumnBy: ReportTableColumnBy;
  dealTableGroupBy: DealTableGroupBy;

  // Cubics moved here so they are saved in bookmarks
  cubicSnapshot: ICubic;
  cubicDeal: ICubic;
  cubicBenchmark: ICubic;
  cubicDashboard: ICubic;
  cubicDealDashboard: ICubic;
  cubicCashflow: ICubic;

  // Deal graph queries
  mainParameter: string[];
  /** Timeline / deal graph grouping (nullable); table uses nested `groupBy`. */
  groupByGraph: string[] | null;
  columnType: "stacked" | "side-by-side";
  secondaryParameter: string[] | null;
  interval: Interval;
  cumulative: boolean;
  cashflowSetId: number | null;

  //benchmark
  graphBenchmark: BenchmarkLiquidityGraphParams;
  benchmarkTableColumns: ReportTableColumn[];
  benchmarkIndexName: BenchmarkIndexPME | null;
}

export type RefetchReport = "deal" | "portfolio" | "comparison" | "timeline";

export type Interval = "month" | "quarter" | "year";

export type ExchangeMethod = "toDate" | "realDate" | "filter";

export type ReduceMethod = "sum" | "average" | "last" | "first" | "none";

export interface MeasureClass {
  id: number;
  title: string;
  measureClass?: MeasureClass["id"];
}

export type ReportsFileDataType =
  | "data"
  | "metadata"
  | "measure"
  | "characteristic"
  | "investor"
  | "measure_asset_class"
  | "investor_characteristic"
  | "investor_rounds_characteristic";
export interface AssetClass {
  id: number;
  title: string;
  characteristics: string[];
  measures: (string | number)[];
  parent_id?: number | null;
}

export type CubicData = {
  name: string;
  value: string | number;
  title: string;
  tooltip?: string;
  filter: string;
  apply_number_format: boolean;
  indexName?: { index: string; name: string };
  pme_warning?: string;
};

export type CubicPageType =
  | "cubicSnapshot"
  | "cubicDeal"
  | "cubicBenchmark"
  | "cubicDashboard"
  | "cubicDealDashboard"
  | "cubicInvestorView"
  | "cubicCashflow"
  | "document";

export interface CubicPayload {
  title: string;
  filter: string;
  // key: string;
  indexName?: { index: string; name: string };
  // pageType: CubicPageType;
}

export interface SharedDeal {}

export type FlatReportsTableData = TableData<
  {
    data?: Record<string, string>; // Deprecated: kept for backward compatibility, use valueData instead
    valueData: Record<string, number | string | null>;
    title: string;
    deal_id: number;
    files?: {
      id: number;
      name: string;
      file_url: string;
    }[];
  },
  {
    key: string;
    title: string;
    postfix: string;
  }
>;

export type CashflowGraph = [number, number, number, number, number][];

export type ReportPredictionTableCell = { user_id: number | null; value: string | null };

export type ReportPredictionTableRow = {
  id: number | null;
  type: "asset_class" | "sub_asset_class" | "deal" | null;
  name: string;
  children?: ReportPredictionTableRow[];
} & {
  [key: string]: ReportPredictionTableCell | number | ReportPredictionTableRow[] | undefined;
};

export type ReportPredictionTable = {
  columns: Array<{ key: string; title: string }[]>;
  rows: ReportPredictionTableRow[];
};

export type ReportCashflowSetting = {
  id: number;
  account_id: number;
  set_id: number;

  preset_id: number | null;

  asset_class_id?: number;
  sub_asset_class_id?: number;
  deal_id?: number;

  contribution_frequency: string;
  bow_factor: number;
  growth_rate: number;
  yield: number;
  investment_period: number;
  contribution_rates: number[];

  created_at: string;
  updated_at: string;
  deleted_at: string | null;
};

export type ReportCashflowSet = {
  id: number;
  account_id: number;
  user_id: number | null;
  name: string;
  updated_at: string;
  created_at: string;
  params: string | null;
};

export type ReportCashflowPreset = {
  id: number;
  name: string;
  contribution_frequency: string;
  bow_factor: number;
  growth_rate: number;
  yield: number;
  investment_period: number;
  contribution_rates: number[];
};

export type ReportCashflowAum = {
  id: number;
  account_id: number;
  set_id: number;
  user_id: number;
  aum_enabled: boolean;
  total_aum: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
};

export type UpdateCashflowAum = {
  set_id: number;
  total_aum: number;
  aum_enabled: boolean;
};

export type ReportCashflowAllocationTarget = {
  id: number;
  account_id: number;
  aum_id: number;
  asset_class_id: number;
  asset_class_name: string;
  share: string;
  years: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
};

export type UpdateAllocationTargetPayload = {
  id: number | string;
  asset_class_id: number;
  share: number;
  years: number;
  is_deleted: boolean;
};

export type ReportAllocationTableRow = {
  [key: string]: number | string;
};

export type ReportAllocationTable = {
  columns: Array<{ key: string; title: string }>;
  rows: Array<{
    asset_class: string;
    [key: string]: number | string;
  }>;
};

export enum ReportPageType {
  Benchmark = "benchmark",
  Snapshot = "snapshot",
  Classic = "classic",
  Comparison = "comparison",
  Timeline = "timeline"
}

export type CorrelationMatrixSettings = {
  dateRange: { from: string; to: string };
  items: IPortfolioConstructionCorrelationItem[];
  // indexes: string[];
  // filters: Record<string, number[]>; // { "Asset Class": string[], "Sub Asset Class": string[], "deal_name": string[] }
};

// Eyal's code
/**
 * Report Configuration - the configuration JSON structure
 */
export interface ReportConfiguration {
  source: {
    invoke: string;
    parameters_schema: Record<string, string>;
  };
  output?: {
    template_id?: number;
    template_tokens?: Record<string, string>;
    type: "pdf" | "excel" | "csv" | "xml" | "json";
    file_name_template?: string;
    delivery_method?: "download" | "email" | "api_response";
  };
  conditions_schema?: ReportCondition;
  client_triggers?: ClientTrigger[];
  tooltip?: string;
}

/**
 * Report Condition - the conditions schema structure
 */
export type ReportCondition = SimpleCondition | AndCondition | OrCondition | NotCondition;

export interface SimpleCondition {
  condition:
    | "context_field_equals"
    | "context_field_not_equals"
    | "context_field_in"
    | "context_field_contains"
    | "context_field_array_length_gte"
    | "eval"
    | "has_single_investor"
    | "user_has_role"
    | "user_has_permission"
    | "account_has_feature";
  field?: string;
  value?: string | number | boolean | null;
  values?: Array<string | number | boolean | null>;
  code?: string;
  role?: string;
  permission?: string;
  feature?: string;
  deal_id_field?: string;
}

/**
 * Client trigger - runs a client-side procedure before report generation
 */
export interface ClientTrigger {
  condition?: ReportCondition;
  execute: string; // e.g. "prepost_report($row)"
}

export interface AndCondition {
  condition: "AND";
  rules: ReportCondition[];
}

export interface OrCondition {
  condition: "OR";
  rules: ReportCondition[];
}

export interface NotCondition {
  condition: "NOT";
  rule: ReportCondition;
}

/**
 * Report - individual report definition
 */
export interface Report {
  id: number;
  report_group_id: number;
  account_id: number;
  name: string;
  description?: string;
  order: number;
  configuration: ReportConfiguration;
  created_at: string;
  updated_at: string;
  deleted_at?: string;
}

/**
 * Report Context - context data passed from frontend
 */
export interface ReportContext {
  deal_id?: number;
  batch_id?: number;
  measure_data_id?: number;
  parent_id?: number | null;
  is_child?: boolean;
  children_count?: number;
  prime_role?: string;
  investor_id?: number;
  /** Row data when opened from measure data table */
  row?: Record<string, unknown>;
  /** Investor from store (with bank_details) when investor_id is set */
  investor?: {
    account?: string;
    bank_details?: Array<{
      id: number;
      account_number: string | null;
      account_name: string | null;
      bank_name: string | null;
    }>;
  };
  [key: string]: string | number | boolean | null | undefined | Record<string, unknown> | unknown[] | unknown;
}

export * from "./api";
export * from "./quick-view";
export * from "./benchmark";
export * from "./report-generator";
